/*    
<summary>
   This class component is all about Managing truck data functionality.
   Developer:Aashish Singh, Created Date:04-April-2024
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/

import { action, computed, makeObservable, observable } from "mobx";
import moment from "moment";
import { toast } from "react-toastify";
import {
  DATE_TIME_FORMAT,
  DATE_TIME_FORMAT_FILENAME,
  DATE_TIME_FORMAT_TO_BACKEND,
} from "../../constants/common-constant";
import UrlConstants from "../../constants/url.constant";
import { getClientId, getClientName } from "../../helpers/localStorages.helper";
import {
  IObservableInitialState,
  IOption,
  ITruckOption,
} from "../../models/ICommon";
import { ITruckFilter, ITruckInitialFilterState } from "../../models/IFilter";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import {
  IAddEditTruck,
  IAssetDownlink,
  IGetTruckList,
  ISelectOption,
  ITruck,
  ITruckList,
} from "../../models/response/ITruckResponse";
import { formatMessage } from "../../translations/formatMessage";
import { addEditTruckState as initialState } from "../initialState/add-edit-truck-state";
import { getTruckListState } from "../initialState/get-truck-list-state";
import { truckInitialFilterState } from "../initialState/initial-filter-states";
import * as services from "../service/base-service";

export class TruckStore {
  inProgress = false;
  error = "";
  truckList: IGetTruckList = getTruckListState;
  truckData: ITruck[] = [];

  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };

  addUpdateTruckState: IObservableInitialState = { ...this.initialStateValue };
  DeleteTruckState: IObservableInitialState = { ...this.initialStateValue };
  sendAssetInfoTruckState: IObservableInitialState = {
    ...this.initialStateValue,
  };
  readAssetInfoTruckState: IObservableInitialState = {
    ...this.initialStateValue,
  };
  sendReadAssetInfoTruckState: IObservableInitialState = {
    ...this.initialStateValue,
  };
  readAssetInfoTruckStateDetails: {
    Id: number;
    AssetId: string;
    DownLinkInformation: string;
    UpdatedAt: string;
  } = {
      Id: 0,
      AssetId: "",
      DownLinkInformation: "",
      UpdatedAt: "",
    };

  truckDetail: ITruck | undefined = undefined;
  truckDetailState: IObservableInitialState = { ...this.initialStateValue };
  filter: ITruckFilter = {
    ...truckInitialFilterState,
  };
  constructor() {
    makeObservable(this, {
      inProgress: observable,
      error: observable,
      truckList: observable,
      addUpdateTruckState: observable,
      DeleteTruckState: observable,
      truckDetail: observable,
      truckDetailState: observable,
      sendAssetInfoTruckState: observable,
      truckData: observable,
      readAssetInfoTruckStateDetails: observable,
      readAssetInfoTruckState: observable,
      sendReadAssetInfoTruckState: observable,
      GetAllTruckService: action,
      GetTruckListService: action,
      GetTruckByIdService: action,
      AddTruckService: action,
      UpdateTruckService: action,
      DeleteTruckService: action,
      SendDownlinkAssetInformation: action,
      reset: action,
      resetStore: action,
      resetGetTruckDetail: action,
      resetAddUpdateTruckState: action,
      resetDeleteTruckState: action,
      ReadDownLinkAssetInformationForAssetId: action,
      SendReadDownLinkAssetInformation: action,
      resetreadAssetInfoTruckStateDetails: action,
      getTruckDetail: computed,
      getTruckList: computed,
      getReadAssetInfoTruckStateDetails: computed,
      availableTruckOptions: computed,
      availableTruckOptionsWithAll: computed,
      getReadAssetInfoTruckStateDetailsDownLinkInformation: computed,
    });
  }

  GetAllTruckService = () => {
    this.inProgress = true;
    const clientIdFromLocalStorage = getClientId() != null ? getClientId() : -1;
    const url = `${UrlConstants.GetTruckSummary}?clientId=${clientIdFromLocalStorage}`;
    return services
      .get(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        this.truckData = response.data.Data;
      })
      .catch((err: string) => {
        toast.error(formatMessage(err));
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  get availableTruckOptions(): ITruckOption[] {
    const options: ITruckOption[] = [];
    if (this.truckData && this.truckData?.length > 0)
      this.truckData.map((truck) => {
        options.push({
          Id: truck.Name ?? "",
          Name: truck.Name ?? "",
        });
      });
    return options;
  }

  get availableTruckOptionsWithAll(): ITruckOption[] {
    const options: ITruckOption[] = [];
    if (this.truckData && this.truckData?.length > 0) {
      options.push({
        Id: "All",
        Name: "All",
      });
      this.truckData.map((truck) => {
        options.push({
          Id: truck.Name ?? "",
          Name: truck.Name ?? "",
        });
      });
    }
    return options;
  }

  GetTruckListService = (
    currentPage: number,
    pagerSize: number,
    orderBy: string,
    column: string,
    filter: ITruckInitialFilterState
  ) => {
    this.inProgress = true;
    const clientIdFromLocalStorage = getClientId() != null ? getClientId() : -1;
    const url =
      UrlConstants.GetTruckList +
      "?clientId=" +
      clientIdFromLocalStorage +
      "&pageNo=" +
      currentPage +
      "&pageSize=" +
      pagerSize +
      "&column=" +
      column +
      "&orderBy=" +
      orderBy;
    // const url = `${UrlConstants.GetTruckList}?clientId=${clientIdFromLocalStorage}`;
    // this.truckList = demoTruck;
    // this.inProgress = false;
    const requestBody = { AssetId: filter.TruckId };
    return services
      .post(url, requestBody)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        this.truckList = response.data.Data;
      })
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  get getTruckList(): ITruckList[] {
    if (this.truckList?.Assets && this.truckList.Assets?.length > 0) {
      return this.truckList.Assets.map((truck: ITruck) => {
        let IsDelete: boolean = true;
        let IsEdit: boolean = true;
        return {
          ...truck,
          Compartment: truck.Compartment === 0 ? 1 : 2,
          InventorySource:
            truck.InventorySource === 0 ? "Ticket" : "Level Sensor",
          IsDeletable: IsDelete,
          IsEditable: IsEdit,
        };
      });
    }
    return [];
  }

  get allTruckSelectOptions(): ISelectOption[] {
    const truckOption: ISelectOption[] = [];
    if (this.truckList && this.truckList.Assets.length > 0)
      this.truckList.Assets.map((truck) => {
        truckOption.push({
          id: truck.Id,
          value: truck.AssetId,
          label: truck.AssetId,
          disabled: false,
          data: truck,
          topic: "",
        });
      });
    return truckOption;
  }

  GetTruckByIdService = (id: number) => {
    this.truckDetailState.inProgress = true;
    const clientIdFromLocalStorage = getClientId() != null ? getClientId() : -1;
    const url = `${UrlConstants.GetTruckById}?id=${id}&clientId=${clientIdFromLocalStorage}`;
    // const tempDetail = this.truckList.Trucks.filter((d:ITruck)=>d.TruckId === id);
    // this.truckDetail = tempDetail.length > 0 ? {...tempDetail[0]} : undefined;
    return services
      .get(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        this.truckDetail = response.data.Data;
      })
      .catch((err: string) => {
        toast.error(formatMessage(err));
        this.truckDetailState.error = err;
      })
      .finally(
        action(() => {
          this.truckDetailState.inProgress = false;
        })
      );
  };

  get getTruckDetail(): IAddEditTruck {
    if (this.truckDetail) {
      return {
        ...this.truckDetail,
        DeviceId:
          this.truckDetail.DeviceId === null ? "" : this.truckDetail.DeviceId,
        InventorySource:
          this.truckDetail.InventorySource === null
            ? 0
            : this.truckDetail.InventorySource,
        SimCard:
          this.truckDetail.SimCard === null ? "" : this.truckDetail.SimCard,
        IpAddress:
          this.truckDetail.IpAddress === null ? "" : this.truckDetail.IpAddress,
        InstallationDate:
          this.truckDetail.InstallationDate === null
            ? ""
            : this.truckDetail.InstallationDate,
        AccountId:
          this.truckDetail.AccountId === null ? "" : this.truckDetail.AccountId,
        TruckId:
          this.truckDetail.AssetId === null ? "" : this.truckDetail.AssetId,
        AssetId:
          this.truckDetail.AssetId === null ? "" : this.truckDetail.AssetId,
        AssetType:
          this.truckDetail.AssetType === null ? "" : this.truckDetail.AssetType,
        Equipment:
          this.truckDetail.Equipment === null ? "" : this.truckDetail.Equipment,
        Compartment:
          this.truckDetail.Compartment === null
            ? 0
            : this.truckDetail.Compartment,
        ExpiryDate:
          this.truckDetail.ExpiryDate === null
            ? ""
            : this.truckDetail.ExpiryDate,
        Rfid: this.truckDetail.Rfid === null ? "" : this.truckDetail.Rfid,
        IsRfidEnabled:
          this.truckDetail.IsRfidEnabled === null
            ? false
            : this.truckDetail.IsRfidEnabled,
      };
    }
    return initialState;
  }

  resetGetTruckDetail = () => {
    this.truckDetail = undefined;
    this.truckDetailState = { ...this.initialStateValue };
  };

  AddTruckService = (id: number, data: IAddEditTruck) => {
    this.addUpdateTruckState.inProgress = true;
    const clientIdFromLocalStorage = getClientId() != null ? getClientId() : -1;
    let url = `${UrlConstants.AddTruck}?clientId=${clientIdFromLocalStorage}`;
    let requestBody = {
      ...data,
      ClientId: Number(clientIdFromLocalStorage),
      AssetId: data.TruckId,
      AccountId: String(clientIdFromLocalStorage),
    };
    return services
      .post(url, { ...requestBody })
      .then(() => {
        this.addUpdateTruckState.success = true;
      })
      .catch((err: string) => {
        if (err.includes(":")) {
          let errorMess = err.split(":");
          toast.error(
            formatMessage(errorMess[0]?.trim()) + " : " + errorMess[1]
          );
        } else {
          toast.error(formatMessage(err));
        }
      })
      .finally(
        action(() => {
          this.addUpdateTruckState.inProgress = false;
        })
      );
  };

  UpdateTruckService = (id: number, data: IAddEditTruck) => {
    this.addUpdateTruckState.inProgress = true;
    const clientIdFromLocalStorage = getClientId() != null ? getClientId() : -1;
    let url = `${UrlConstants.UpdateTruck}?id=${id}&clientId=${clientIdFromLocalStorage}`;
    // let url = `${UrlConstants.UpdateTruck}?clientId=${clientIdFromLocalStorage}`;
    let requestBody = {
      ...data,
      ClientId: Number(clientIdFromLocalStorage),
      AssetId: data.TruckId,
      AccountId: String(clientIdFromLocalStorage),
    };
    return services
      .put(url, { ...requestBody })
      .then(() => {
        this.addUpdateTruckState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateTruckState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateTruckState.inProgress = false;
        })
      );
  };

  downloadWriteLog = (data: any) => {
    const currentDate = new Date();
    const logContent = this.generateWriteLogContent(data.Data);
    const blob = new Blob([logContent], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download =
      moment(currentDate).format(DATE_TIME_FORMAT_FILENAME) +
      "DownlinkAssetWriteMsglog.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  generateWriteLogContent = (data: any) => {
    const currentDate = new Date();

    const generateErrorList = (
      values: string[] | number,
      itemsPerLine: number = 4
    ) => {
      if (!Array.isArray(values) || values.length === 0) {
        return "None";
      }

      const formattedList = values.map((item, index) => {
        const separator = (index + 1) % itemsPerLine === 0 ? "\n" : ", ";
        return `${item}${separator}`;
      });

      return formattedList.join("").trim();
    };

    const logContent = `
  Log generated on: ${moment(currentDate).format(DATE_TIME_FORMAT_TO_BACKEND)}
  Total downlink asset write message sent Count: ${data.TotalDownlinkWriteMsgSentCount
      }
  Downlink asset write message Already in progress: ${data.DownlinkWriteMsgInProgressCount
      }
  New downlink asset write message sent: ${data.NewDownlinkWriteMsgSentCount}
  
  **Assets with downlink write message Already in progress: (Count: ${data.DownlinkWriteMsgInProgressCount
      }) ** :
   ${data?.DownlinkWriteMsgInProgress?.length > 0
        ? generateErrorList(data.DownlinkWriteMsgInProgress)
        : 0
      }
    **Vehicle not found belongs to asset id: (Count: ${data.DownlinkVehicleAssetIdNotFound?.length
      }) ** :
   ${data?.DownlinkVehicleAssetIdNotFound?.length > 0
        ? generateErrorList(data.DownlinkVehicleAssetIdNotFound)
        : 0
      }
  `;
    return logContent;
  };

  downloadDeleteLog = (data: any) => {
    const currentDate = new Date();
    const logContent = this.generateDeleteLogContent(data.Data);
    const blob = new Blob([logContent], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download =
      moment(currentDate).format(DATE_TIME_FORMAT_FILENAME) +
      "DownlinkAssetDeleteMsglog.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  generateDeleteLogContent = (data: any) => {
    const currentDate = new Date();

    const generateErrorList = (
      values: string[] | number,
      itemsPerLine: number = 4
    ) => {
      if (!Array.isArray(values) || values.length === 0) {
        return "None";
      }

      const formattedList = values.map((item, index) => {
        const separator = (index + 1) % itemsPerLine === 0 ? "\n" : ", ";
        return `${item}${separator}`;
      });

      return formattedList.join("").trim();
    };

    const logContent = `
  Log generated on: ${moment(currentDate).format(DATE_TIME_FORMAT_TO_BACKEND)}
  Total downlink asset delete message sent Count: ${data.TotalDownlinkDeleteMsgSentCount
      }
  Downlink asset delete message Already in progress: ${data.DownlinkDeleteMsgInProgressCount
      }
  New downlink asset delete message sent: ${data.NewDownlinkDeleteMsgSentCount}
  
  **Assets with downlink delete message Already in progress: (Count: ${data.DownlinkDeleteMsgInProgressCount
      }) ** :
   ${data?.DownlinkDeleteMsgInProgress?.length > 0
        ? generateErrorList(data.DownlinkDeleteMsgInProgress)
        : 0
      }
  `;
    return logContent;
  };

  SendDownlinkAssetInformation = (data: IAssetDownlink, apiType: number) => {
    this.sendAssetInfoTruckState.inProgress = true;
    const clientIdFromLocalStorage = getClientId() != null ? getClientId() : -1;
    let url =
      apiType === 0
        ? `${UrlConstants.SendDownlinkWriteAssetInformation}`
        : `${UrlConstants.SendDownlinkDeleteAssetInformation}`;
    let requestBody = {
      ClientId: Number(clientIdFromLocalStorage),
      AssetDownlinkRequestItemArrayList: data.AssetDownlinkRequestItemArrayList,
    };
    return services
      .post(url, { ...requestBody })
      .then((response: any) => {
        this.sendAssetInfoTruckState.success = true;
        toast.success(formatMessage("Information Sent Successfully"));
        if (apiType == 0) this.downloadWriteLog(response.data);
        else this.downloadDeleteLog(response.data);
      })
      .catch((err: string) => {
        this.sendAssetInfoTruckState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.sendAssetInfoTruckState.inProgress = false;
        })
      );
  };

  ReadDownLinkAssetInformationForAssetId = (
    data: ITruckList,
    clientList: IOption[]
  ) => {
    this.readAssetInfoTruckState.inProgress = true;
    const clientIdFromLocalStorage = getClientId() != null ? getClientId() : -1;
    let url = `${UrlConstants.ReadDownLinkAssetInformation}`;
    let requestBody = {
      ClientId: clientList.filter(
        (client: IOption) => client.Id === Number(clientIdFromLocalStorage)
      )[0]
        ? clientList.filter(
          (client: IOption) => client.Id === Number(clientIdFromLocalStorage)
        )[0].Name
        : getClientName(),
      AssetId: data.AssetId,
      AssetIdLong: data.Id,
      ClientIdLong: Number(clientIdFromLocalStorage),
      DeviceId: data.DeviceId,
    };
    return services
      .post(url, { ...requestBody })
      .then((response: any) => {
        this.readAssetInfoTruckState.success = true;
        // toast.success(formatMessage("Information Sent Successfully"));
        this.readAssetInfoTruckStateDetails =
          response.data.Data.AssetDownLinkInformation;
      })
      .catch((err: string) => {
        this.readAssetInfoTruckState.error = err;
        // toast.error(formatMessage(err));
        // this.readAssetInfoTruckStateDetails = {
        //   Id: -1,
        //   AssetId: "-",
        //   DownLinkInformation: "Details not avaiable",
        //   UpdatedAt: "Dtails not available",
        // };
      })
      .finally(
        action(() => {
          this.readAssetInfoTruckState.inProgress = false;
        })
      );
  };
  get getReadAssetInfoTruckStateDetails() {
    if (this.readAssetInfoTruckStateDetails) {
      return {
        ...this.readAssetInfoTruckStateDetails,
        AssetId:
          this.readAssetInfoTruckStateDetails.AssetId === null
            ? ""
            : this.readAssetInfoTruckStateDetails.AssetId,
        DownLinkInformation:
          this.readAssetInfoTruckStateDetails.DownLinkInformation === null
            ? ""
            : this.readAssetInfoTruckStateDetails.DownLinkInformation,
        UpdatedAt:
          this.readAssetInfoTruckStateDetails.UpdatedAt === null
            ? ""
            : moment(this.readAssetInfoTruckStateDetails.UpdatedAt).format(
              DATE_TIME_FORMAT
            ),
      };
    }
    return {
      Id: 0,
      AssetId: "",
      DownLinkInformation: "",
      UpdatedAt: "",
    };
  }

  get getReadAssetInfoTruckStateDetailsDownLinkInformation() {
    if (this.readAssetInfoTruckStateDetails && this.readAssetInfoTruckStateDetails.DownLinkInformation) {
      let downLinkInformation = JSON.parse(this.readAssetInfoTruckStateDetails.DownLinkInformation)
      return downLinkInformation.map(
        (info: { assetid: any; client_id: any; tid: any; pos: any; vehicles: any[]; }) => {
          return {
            Assetid: info.assetid,
            ClientId: info.client_id,
            TId: info.tid,
            Pos: info.pos,
            Vehicles: info.vehicles.map((vehicle: any) => {
              return {
                Rfid: vehicle.r ? vehicle.r : "-",
                VehicleNumber: vehicle.v
              }
            })
          }
        }
      )
    }
    else {
      return []
    }
  }
  resetreadAssetInfoTruckStateDetails = () => {
    this.readAssetInfoTruckStateDetails = {
      Id: 0,
      AssetId: "",
      DownLinkInformation: "",
      UpdatedAt: "",
    };
  };
  SendReadDownLinkAssetInformation = (
    data: ITruckList,
    clientList: IOption[]
  ) => {
    this.sendReadAssetInfoTruckState.inProgress = true;
    const clientIdFromLocalStorage = getClientId() != null ? getClientId() : -1;
    let url = `${UrlConstants.SendReadDownLinkAssetInformation}`;
    let requestBody = {
      ClientId: clientList.filter(
        (client: IOption) => client.Id === Number(clientIdFromLocalStorage)
      )[0]
        ? clientList.filter(
          (client: IOption) => client.Id === Number(clientIdFromLocalStorage)
        )[0].Name
        : getClientName(),
      AssetId: data.AssetId,
      AssetIdLong: data.Id,
      ClientIdLong: Number(clientIdFromLocalStorage),
      DeviceId: data.DeviceId,
    };
    return services
      .post(url, { ...requestBody })
      .then(() => {
        this.sendReadAssetInfoTruckState.success = true;
        // toast.success(formatMessage("Information Sent Successfully"));
      })
      .catch((err: string) => {
        this.sendReadAssetInfoTruckState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.sendReadAssetInfoTruckState.inProgress = false;
        })
      );
  };

  resetAddUpdateTruckState = () => {
    this.addUpdateTruckState = { ...this.initialStateValue };
  };

  DeleteTruckService = (id: number) => {
    this.DeleteTruckState.inProgress = true;
    const clientIdFromLocalStorage = getClientId() != null ? getClientId() : -1;
    const url = `${UrlConstants.DeleteTruck}?id=${id}&clientId=${clientIdFromLocalStorage}`;
    return services
      .deleteapi(url)
      .then(() => {
        this.DeleteTruckState.success = true;
      })
      .catch((err: string) => {
        this.DeleteTruckState.error = err;
      })
      .finally(
        action(() => {
          this.DeleteTruckState.inProgress = false;
        })
      );
  };

  resetDeleteTruckState = () => {
    this.DeleteTruckState = { ...this.initialStateValue };
  };

  reset = () => {
    this.error = "";
    this.inProgress = false;
  };

  resetError = () => {
    this.error = "";
  };

  /**
   * This function is used to reset all store observables to their initial values.
   * @returns
   */
  resetStore = () => {
    this.error = "";
    this.inProgress = false;
    this.truckList = getTruckListState;

    this.truckDetail = undefined;
    this.truckDetailState = { ...this.initialStateValue };

    this.addUpdateTruckState = { ...this.initialStateValue };

    this.DeleteTruckState = { ...this.initialStateValue };
  };

  /**
   * This function is used to convert truck list data to the corresponding CSV format
   * @param data : Truck List data
   * @returns csvData suitable for creating blob object
   */
  convertToCSV = (data: any[]): string => {
    const header = Object.keys(data[0]).join(",");
    const rows = data.map((obj) => Object.values(obj).join(","));
    return `${header}\n${rows.join("\n")}`;
  };

  /**
   * This function downloads the CSV by dynamically making an <a> tag.
   * @param data : Truck List data
   */
  downloadTruckCSV = (data: ITruckList[]) => {
    const currentDate = new Date();
    if (data.length > 0) {
      const csvData = this.convertToCSV(this.exportTruckCSV(data));
      const blob = new Blob([csvData], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        moment(currentDate).format(DATE_TIME_FORMAT_FILENAME) + " Trucks.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else toast.warn(formatMessage("no_data_to_export"));
  };

  setFilterDetail = (filter: ITruckFilter) => {
    this.filter = { ...filter };
  };

  /**
   * This function is used to convert the truckDetailsList to the required data suitable for convertToCSV function.
   * @param truckDataList : Truck List
   * @returns CSV data suitable for convertToCSV function
   */
  exportTruckCSV(truckDataList: any): ITruckList[] {
    if (truckDataList && truckDataList?.length > 0)
      return truckDataList?.map((truck: ITruck) => {
        return {
          Id: truck.Id,
          DeviceId: truck.DeviceId,
          AccountId: truck.AccountId,
          TruckId: truck.TruckId,
          Compartment: truck.Compartment,
        };
      });
    return [];
  }
}

export default new TruckStore();
